// src/components/Layout/MainLayout.js - VERSÃO FINAL E COMPLETA
import React, { useState, useMemo, startTransition } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar, Menu, MenuItem,
  Chip, useTheme, useMediaQuery, Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon, Timeline as TimelineIcon,
  Assessment as ReportIcon, People as PeopleIcon, Business as BusinessIcon,
  PersonAdd as PersonAddIcon, AccountCircle,
  Logout as LogoutIcon, BarChart as ProductivityIcon,
  DarkMode as DarkModeIcon, LightMode as LightModeIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeContext';
import ReportsDropdown from '../Navigation/ReportsDropdown';

const drawerWidth = 280;

const MainLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, getNivelNome, hasPermission, NIVEIS } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isDark = mode === 'dark';

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { logout(); };

  const getMenuItems = () => {
    const items = [];

    if (hasPermission(NIVEIS.EXPEDICAO)) {
      items.push({ text: 'Linha do Tempo', icon: <TimelineIcon />, path: '/linha-tempo', color: '#2196f3' });
    }
    if (hasPermission(NIVEIS.LABORATORIO)) {
      items.push({ text: 'Produtividade', icon: <ProductivityIcon />, path: '/produtividade', color: '#4caf50' });
      // Menu de Relatórios será renderizado separadamente como componente dropdown
      items.push({ text: 'Funcionários', icon: <PeopleIcon />, path: '/funcionarios', color: '#9c27b0' });
      items.push({ text: 'Setores', icon: <BusinessIcon />, path: '/setores', color: '#607d8b' });
    }
    if (hasPermission(NIVEIS.ADMINISTRADOR)) {
      items.push({ text: 'Cadastro Usuário', icon: <PersonAddIcon />, path: '/usuarios', color: '#f44336' });
    }
    return items;
  };

  const menuItems = useMemo(() => getMenuItems(), [hasPermission, NIVEIS]);

  // Função para obter o título da página baseado na rota
  const getCurrentPageTitle = () => {
    const path = location.pathname;
    if (path.startsWith('/relatorio-producao-setor')) return 'Relatório de Produção por Setor';
    if (path.startsWith('/relatorio-volume-producao')) return 'Relatório de Volume de Produção';
    if (path.startsWith('/relatorio-encapsulacao')) return 'Encapsulação - Volume de Produção';
    if (path.startsWith('/relatorio-performance')) return 'Relatório de Performance Produtiva';
    if (path.startsWith('/relatorio-tabular')) return 'Relatório Tabular';

    const currentRoute = menuItems.find(item => path.startsWith(item.path));
    return currentRoute?.text || 'Dashboard';
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', minHeight: 80 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <DashboardIcon sx={{ mr: 2, fontSize: 32 }} />
          <Box>
            <Typography variant="h6" noWrap component="div" fontWeight="bold">Sistema Controle</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Administração</Typography>
          </Box>
        </Box>
      </Toolbar>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: isDark ? 'grey.900' : 'grey.50', borderRadius: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>{user?.nome?.charAt(0).toUpperCase()}</Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">{user?.nome}</Typography>
            <Chip label={getNivelNome(user?.nivel)} size="small" color={user?.nivel === 3 ? 'error' : user?.nivel === 2 ? 'warning' : 'info'} sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      </Box>
      <Divider />
      <List sx={{ px: 1 }}>
        {menuItems.map((item) => {
          const isSelected = location.pathname.startsWith(item.path);
          return (
            <React.Fragment key={item.text}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => { startTransition(() => navigate(item.path)); if (isMobile) setMobileOpen(false); }}
                  sx={{ borderRadius: 2, mb: 0.5, mx: 1, '&.Mui-selected': { bgcolor: `${item.color}1A`, '&:hover': { bgcolor: `${item.color}2A` }, '& .MuiListItemIcon-root, & .MuiListItemText-primary': { color: item.color, fontWeight: 'bold' } } }}
                >
                  <ListItemIcon sx={{ color: isSelected ? item.color : 'inherit' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isSelected ? 'bold' : 'normal' }} />
                </ListItemButton>
              </ListItem>
              {/* Relatórios logo abaixo de Produtividade */}
              {item.text === 'Produtividade' && hasPermission(NIVEIS.LABORATORIO) && (
                <ReportsDropdown
                  isMobile={isMobile}
                  onNavigate={() => isMobile && setMobileOpen(false)}
                />
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{
        width: { lg: `calc(100% - ${drawerWidth}px)` },
        ml: { lg: `${drawerWidth}px` },
        bgcolor: isDark ? 'background.paper' : 'white',
        color: 'text.primary',
        boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { lg: 'none' } }}><MenuIcon /></IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>{getCurrentPageTitle()}</Typography>
          <Tooltip title={isDark ? 'Modo Claro' : 'Modo Escuro'}>
            <IconButton onClick={toggleTheme} color="inherit" sx={{ mr: 1 }}>
              {isDark ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          <IconButton size="large" edge="end" aria-label="account of current user" aria-haspopup="true" onClick={handleProfileMenuOpen} color="inherit"><AccountCircle /></IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleProfileMenuClose} onClick={handleProfileMenuClose}>
            <MenuItem onClick={handleLogout}><ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>Sair</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { lg: drawerWidth }, flexShrink: { lg: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', lg: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}>{drawer}</Drawer>
        <Drawer variant="permanent" sx={{ display: { xs: 'none', lg: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }} open>{drawer}</Drawer>
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { lg: `calc(100% - ${drawerWidth}px)` }, minHeight: '100vh', bgcolor: 'background.default' }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;
